<template>
  <div class="ddv custom-legend" :class="{ horizontal }">
    <div v-if="slots.header">
      <slot name="header" />
    </div>
    <template v-if="modelValue && model && model.length">
      <template v-if="xLabel || sharedXLabel || (yLabel && !hideYDetails)">
        <div class="operating-point-table">
          <div class="operating-point-header">
            <div class="operating-point-header-label">
              <label>&nbsp;</label>
              <div class="operating-point-background"></div>
            </div>
          </div>
          <div class="operating-point-table">
            <div v-if="xLabel" class="operating-point-row">
              <span v-if="xLabel">{{ xLabel }}</span>
              <span v-else>&nbsp;</span>
            </div>
            <div v-if="sharedXLabel" class="operating-point-row">
              <span class="operating-point-label">{{ sharedXLabel }}</span>
              <span class="operating-point-value">{{
                typeof sharedXValue === "number" ? format(sharedXValue) : ""
              }}</span>
            </div>
            <div v-if="yLabel && !hideYDetails" class="operating-point-row">
              <span v-if="yLabel">{{ yLabel }}</span>
              <span v-else>&nbsp;</span>
            </div>
          </div>
        </div>
      </template>
      <template v-for="(item, index) in model" :key="index">
        <slot name="item" v-bind="{ item, index }"></slot>
        <template v-if="!slots.item">
          <!-- Operating Points Layout -->
          <div v-if="'point' in item" class="operating-point-table">
            <div
              class="operating-point-header"
              :style="
                item.color
                  ? `--activeColor: ${item.color}`
                  : `--activeColor: var(--plotColor${index})`
              "
            >
              <CustomCheckbox
                v-if="!static"
                :input-id="`${legendId}-${index}`"
                class="operating-point-control"
                :model-value="modelValue[index]"
                :value="index"
                :style="
                  item.color
                    ? `--activeColor: ${item.color}`
                    : `--activeColor: var(--plotColor${index})`
                "
                binary
                type="checkbox"
                :name="item.label"
                @update:model-value="handleUpdate($event, index)"
              />
              <div class="operating-point-header-label">
                <label :for="`${legendId}-${index}`">{{ item.label }}</label>
                <div class="operating-point-background"></div>
              </div>
            </div>
            <div
              class="operating-point-table"
              :style="
                item.color
                  ? `--activeColor: ${item.color}`
                  : `--activeColor: var(--plotColor${index})`
              "
            >
              <div
                v-if="!((xLabel || sharedXLabel) && item.hideXDetails)"
                class="operating-point-row"
              >
                <span class="operating-point-label">{{
                  item.xLabel || "X"
                }}</span>
                <span class="operating-point-value">{{
                  format(item.point.x)
                }}</span>
              </div>
              <div
                v-if="!(hideYDetails || item.hideYDetails)"
                class="operating-point-row"
              >
                <span class="operating-point-label">{{
                  item.yLabel || "Y"
                }}</span>
                <span class="operating-point-value">{{
                  format(item.point.y)
                }}</span>
              </div>
            </div>
          </div>

          <!-- Thresholds Layout -->
          <div v-if="'value' in item" class="operating-point-table">
            <!-- Numeric Threshold -->
            <template v-if="typeof item.value === 'number'">
              <div
                class="operating-point-header"
                :style="
                  item.color
                    ? `--activeColor: ${item.color}`
                    : `--activeColor: var(--plotColor${index})`
                "
              >
                <CustomCheckbox
                  v-if="!static"
                  :input-id="`${legendId}-${index}`"
                  class="operating-point-control"
                  :model-value="modelValue[index]"
                  :value="index"
                  :style="
                    item.color
                      ? `--activeColor: ${item.color}`
                      : `--activeColor: var(--plotColor${index})`
                  "
                  binary
                  type="checkbox"
                  :name="item.label"
                  @update:model-value="handleUpdate($event, index)"
                />
                <div class="operating-point-header-label">
                  <label :for="`${legendId}-${index}`">{{ item.label }}</label>
                  <div class="operating-point-background"></div>
                </div>
              </div>
              <div
                class="operating-point-table"
                :style="
                  item.color
                    ? `--activeColor: ${item.color}`
                    : `--activeColor: var(--plotColor${index})`
                "
              >
                <div class="operating-point-row">
                  <span class="operating-point-label">Value</span>
                  <span class="operating-point-value">{{
                    item.value.toFixed(2)
                  }}</span>
                </div>
              </div>
            </template>
            <!-- Domain Threshold -->
            <template v-else-if="'min' in item.value">
              <div
                class="operating-point-header"
                :style="
                  item.color
                    ? `--activeColor: ${item.color}`
                    : `--activeColor: var(--plotColor${index})`
                "
              >
                <CustomCheckbox
                  v-if="!static"
                  :input-id="`${legendId}-${index}`"
                  class="operating-point-control"
                  :model-value="modelValue[index]"
                  :value="index"
                  :style="
                    item.color
                      ? `--activeColor: ${item.color}`
                      : `--activeColor: var(--plotColor${index})`
                  "
                  binary
                  type="checkbox"
                  :name="item.label"
                  @update:model-value="handleUpdate($event, index)"
                />
                <div class="operating-point-header-label">
                  <label :for="`${legendId}-${index}`">{{ item.label }}</label>
                  <div class="operating-point-background"></div>
                </div>
              </div>
              <div
                class="operating-point-table"
                :style="
                  item.color
                    ? `--activeColor: ${item.color}`
                    : `--activeColor: var(--plotColor${index})`
                "
              >
                <div class="operating-point-row">
                  <span class="operating-point-label">Min</span>
                  <span class="operating-point-value">{{
                    format(item.value.min)
                  }}</span>
                </div>
                <div class="operating-point-row">
                  <span class="operating-point-label">Max</span>
                  <span class="operating-point-value">{{
                    format(item.value.max)
                  }}</span>
                </div>
              </div>
            </template>
          </div>

          <!-- Data Sets -->
          <template v-else-if="'points' in item">
            <div class="control" :class="{ static }">
              <CustomCheckbox
                :input-id="`${legendId}-${index}`"
                :model-value="modelValue[index]"
                :value="index"
                :style="
                  item.color
                    ? `--activeColor: ${item.color}`
                    : `--activeColor: var(--plotColor${index})`
                "
                binary
                @input="handleUpdate($event, index)"
              />
              <label :for="`${legendId}-${index}`" class="select-none">
                <slot name="label" v-bind="{ item, index }">
                  {{ item.title }}
                </slot>
              </label>
            </div>
          </template>
        </template>
      </template>
    </template>
    <div v-if="slots.footer">
      <slot name="footer" />
    </div>
  </div>
</template>

<script setup lang="ts">
import CustomCheckbox from "@/components/primevueless/CustomCheckbox.vue";
import type { OperatingPoint } from "@/components/types";
import { getDefaultValueFormatter } from "@incytestudios/ddv";
import type { DataSet, Threshold } from "@incytestudios/ddv/types/common";

import { computed, ref, useSlots } from "vue";

const slots = useSlots();

const props = defineProps<{
  static?: boolean;
  disabled?: boolean;
  horizontal?: boolean;
  model?: DataSet[] | OperatingPoint[] | Threshold[];
  modelValue?: boolean[];
  xLabel?: string;
  sharedXLabel?: string;
  sharedXValue?: number;
  yLabel?: string;
  hideYDetails?: boolean;
}>();

const hideYDetails = computed(() => props.hideYDetails ?? false);

const emit = defineEmits<{
  (e: "update:model-value", value: boolean[]): void;
}>();

const legendId = ref(`CustomLegend-${Math.ceil(Math.random() * 10000)}`);

const handleUpdate = (e: boolean, i: number) => {
  if (props.modelValue) {
    const newVal = [...props.modelValue];
    newVal.splice(i, 1, e);
    emit("update:model-value", newVal);
  }
};

const format = getDefaultValueFormatter();
</script>

<style lang="scss" scoped>
.custom-legend {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  flex-grow: 1;
  gap: 1em;
  padding: 1em;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;

  &.horizontal {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
  }

  .operating-point-table {
    display: flex;
    flex-direction: column;
    max-width: 200px;
    .operating-point-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .operating-point-header-label {
        position: relative;
        font-size: 14px;
        font-weight: 500;
        z-index: 1;
        width: 100%;
        height: 100%;
        flex-grow: 1;
        padding: 0.25em;
      }

      .operating-point-background {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        border-radius: 4px;
        opacity: 0.4;
        z-index: -1;
        background-color: var(--activeColor);
      }

      label {
        z-index: 1;
        user-select: none;
      }

      .operating-point-control {
        margin-right: 0.25em;
      }
    }
    .operating-point-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 14px;
      font-weight: 400;
      padding: 0 5px;

      .operating-point-label {
        margin-right: 14px;
      }
      .operating-point-value {
        color: var(--activeColor);
        font-family: var(--axisTickFontFamily);
        font-size: 0.75em;
        font-weight: var(--axisTickFontWeight);
      }
    }
  }

  .operating-point-table,
  :slotted(.operating-point-table) {
    width: auto;

    .operating-point-row {
      display: flex;
      justify-content: space-between;
      min-width: 120px;
    }

    .operating-point-label {
      font-weight: 400;
      text-align: left;
    }

    .operating-point-value {
      font-family: var(--axisTickFontFamily);
      font-size: 0.75em;
      font-weight: var(--axisTickFontWeight);
      text-align: left;
      color: var(--activeColor);
    }
  }

  .control,
  :slotted(.control) {
    display: flex;
    gap: 0.5em;
    align-items: center;

    &.disabled {
      pointer-events: none;
    }

    &.static {
      pointer-events: none;
    }
  }

  :deep(.p-checkbox .p-checkbox-box.p-highlight) {
    border-color: var(--activeColor);
    background: var(--activeColor);
  }
}
</style>
